import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { triggerReflow } from "../lib/util";
import { shared } from "../styles";
import { Scroller } from "./scroller";

type PopoverAlignment =
    | "bottom"
    | "bottom-left"
    | "bottom-right"
    | "right"
    | "right-top"
    | "right-bottom"
    | "top"
    | "top-left"
    | "top-right"
    | "left"
    | "left-top"
    | "left-bottom"
    | "auto";

interface Position {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

const ALIGNMENTS: PopoverAlignment[] = [
    "bottom",
    "right",
    "top",
    "left",
    "bottom-left",
    "bottom-right",
    "right-top",
    "right-bottom",
    "top-left",
    "top-right",
    "left-top",
    "left-bottom",
];

function isScrollable(el: HTMLElement) {
    if (el instanceof Scroller) {
        return true;
    }
    const hasScrollableContent = el.scrollHeight > el.clientHeight;

    const overflowYStyle = window.getComputedStyle(el).overflowY;
    const isOverflowHidden = !overflowYStyle || overflowYStyle.indexOf("hidden") !== -1;

    return hasScrollableContent && !isOverflowHidden;
}

function getScrollableParent(el: HTMLElement): HTMLElement {
    if (!el || el === document.body || isScrollable(el)) {
        return el;
    }

    const parentNode = el.parentNode instanceof ShadowRoot ? el.parentNode.host : el.parentNode;

    return getScrollableParent(parentNode as HTMLElement);
}

@customElement("ptc-popover")
export class Popover extends LitElement {
    @property({ attribute: false })
    anchor: string | HTMLElement | null = ":previous";

    @property()
    trigger: "click" | "hover" | "focus" = "click";

    @property()
    alignment: PopoverAlignment = "auto";

    @property()
    preferAlignment: string | string[] = "bottom";

    @property({ type: Boolean, attribute: "hide-on-click" })
    hideOnClick = false;

    @property({ type: Boolean, attribute: "hide-on-leave" })
    hideOnLeave = false;

    @property({ type: Boolean, attribute: "non-interactive", reflect: true })
    nonInteractive = false;

    @property({ reflect: true })
    positioning: "fixed" | "absolute" = "fixed";

    @property({ attribute: false })
    content?: TemplateResult;

    get showing() {
        return this.classList.contains("showing");
    }

    private _anchorElement: HTMLElement;
    private _mutationObserver = new MutationObserver(() => this._contentChanged());
    private _resizeObserver = new ResizeObserver(() => this._contentChanged());

    private _clickHandler = () => this._click();
    private _selfClickHandler = () => this._selfClick();
    private _documentClickHandler = () => this._documentClick();
    private _mouseenterHandler = () => this._mouseenter();
    private _mouseleaveHandler = () => this._mouseleave();
    private _focusHandler = () => this._focus();
    private _blurHandler = () => this._blur();
    private _selfMouseleaveHandler = () => this._selfMouseleave();
    private _mousedownHandler = () => this._mousedown();

    protected _ignoreNextClick = false;
    private _keepOpenUntilClick = false;

    private _hideTimeout: number;

    private _scrollParent: HTMLElement | null = null;
    private _scrollListenerAttached = false;
    private _scrollHandler = () => {
        if (this.showing) {
            this.align();
        }
    };

    // getScrollableParent walks ancestors reading scrollHeight/clientHeight/getComputedStyle,
    // forcing a synchronous layout. Defer it to first open so popovers that never show pay
    // nothing; the scroll listener only re-aligns while showing anyway.
    private _attachScrollListener() {
        if (this._scrollListenerAttached || !this._anchorElement) {
            return;
        }
        this._scrollListenerAttached = true;
        this._scrollParent = getScrollableParent(this._anchorElement);
        this._scrollParent?.addEventListener("scroll", this._scrollHandler);
    }

    private _resolveHidden?: () => void;

    connectedCallback() {
        super.connectedCallback();
        this.style.display = "none";
        if (this.anchor === ":previous") {
            this._anchorElement = this.previousElementSibling as HTMLElement;
        } else if (typeof this.anchor === "string") {
            const root = this.getRootNode() as HTMLElement;
            this._anchorElement = root.querySelector(this.anchor) as HTMLElement;
        } else {
            this._anchorElement = this.anchor as HTMLElement;
        }
        this._addListeners();
    }

    disconnectedCallback() {
        this._removeListeners();
    }

    show(ingoreNextClick = false) {
        this._keepOpenUntilClick = this.trigger === "click";
        if (ingoreNextClick) {
            this._ignoreNextClick = true;
        }
        this._show();
        return new Promise<void>((resolve) => (this._resolveHidden = resolve));
    }

    showAt(anchor: HTMLElement, ignoreNextClick?: boolean) {
        this._removeListeners();
        this.anchor = this._anchorElement = anchor;
        this._addListeners();
        return this.show(ignoreNextClick);
    }

    hide() {
        this._keepOpenUntilClick = this._ignoreNextClick = false;
        this.classList.remove("showing");
        clearTimeout(this._hideTimeout);
        this._hideTimeout = window.setTimeout(() => {
            this.style.display = "none";
            this.style.pointerEvents = "none";
            this.removeAttribute("tabindex");
            this.dispatchEvent(new CustomEvent("popover-hide"));
        }, 300);
        if (this._resolveHidden) {
            this._resolveHidden();
            this._resolveHidden = undefined;
        }
    }

    align() {
        const alignment = this.alignment === "auto" ? this._getAutoAlignment() : this.alignment;
        const pos = this._getPosition(alignment);
        Object.assign(this.style, { top: `${pos.top}px`, left: `${pos.left}px` });

        ALIGNMENTS.forEach((a) => this.classList.toggle(a, a === alignment));
    }

    private _selfClick() {
        if (!this.hideOnClick) {
            this._ignoreNextClick = true;
        }
    }

    private _mousedown() {
        if (!this.matches(":focus-within")) {
            this.setAttribute("tabindex", "-1");
            this.focus();
        }
    }

    private _click() {
        this._ignoreNextClick = true;
        this._show();
    }

    private _documentClick() {
        if (this.showing && this.trigger !== "focus" && !this._ignoreNextClick) {
            this.hide();
            this._keepOpenUntilClick = false;
        }
        this._ignoreNextClick = false;
    }

    private _mouseenter() {
        this._show();
    }

    private _mouseleave() {
        if (!this._keepOpenUntilClick) {
            this.hide();
        }
    }

    private _selfMouseleave() {
        if (this.hideOnLeave) {
            this.hide();
        }
    }

    private _focus() {
        this._show();
    }

    private _blur() {
        this.hide();
    }

    private _show() {
        clearTimeout(this._hideTimeout);
        this._attachScrollListener();
        this.style.display = "";
        this.style.pointerEvents = "";
        triggerReflow(this);
        this.align();
        this.classList.add("showing");
        this.dispatchEvent(new CustomEvent("popover-show"));
    }

    private _getAutoAlignment(): PopoverAlignment {
        const preferred = (
            Array.isArray(this.preferAlignment) ? [...this.preferAlignment] : [this.preferAlignment]
        ).reverse();
        const alignments = [...ALIGNMENTS].sort((a, b) => preferred.indexOf(b) - preferred.indexOf(a));
        return alignments.find((alignment) => this._isWithinBounds(this._getPosition(alignment))) || alignments[0];
    }

    private _getPosition(alignment: PopoverAlignment): Position {
        const pos = { top: 0, left: 0, bottom: 0, right: 0 };

        if (!this._anchorElement) {
            return pos;
        }

        const {
            top: anchorTop,
            left: anchorLeft,
            height: anchorHeight,
            width: anchorWidth,
        } = this.positioning === "absolute"
            ? {
                  top: this._anchorElement.offsetTop,
                  left: this._anchorElement.offsetLeft,
                  width: this._anchorElement.offsetWidth,
                  height: this._anchorElement.offsetHeight,
              }
            : this._anchorElement.getBoundingClientRect();
        const { width: ownWidth, height: ownHeight } = this.getBoundingClientRect();
        const offset = 18;

        switch (alignment) {
            case "bottom":
                pos.top = anchorTop + anchorHeight;
                pos.left = anchorLeft + anchorWidth / 2 - ownWidth / 2;
                break;
            case "bottom-left":
                pos.top = anchorTop + anchorHeight;
                pos.left = anchorLeft + anchorWidth / 2 - ownWidth + offset;
                break;
            case "bottom-right":
                pos.top = anchorTop + anchorHeight;
                pos.left = anchorLeft + anchorWidth / 2 - offset;
                break;
            case "right":
                pos.left = anchorLeft + anchorWidth;
                pos.top = anchorTop + anchorHeight / 2 - ownHeight / 2;
                break;
            case "right-top":
                pos.left = anchorLeft + anchorWidth;
                pos.top = anchorTop + anchorHeight / 2 - ownHeight + offset;
                break;
            case "right-bottom":
                pos.left = anchorLeft + anchorWidth;
                pos.top = anchorTop + anchorHeight / 2 - offset;
                break;
            case "top":
                pos.top = anchorTop - ownHeight;
                pos.left = anchorLeft + anchorWidth / 2 - ownWidth / 2;
                break;
            case "top-left":
                pos.top = anchorTop - ownHeight;
                pos.left = anchorLeft + anchorWidth / 2 - ownWidth + offset;
                break;
            case "top-right":
                pos.top = anchorTop - ownHeight;
                pos.left = anchorLeft + anchorWidth / 2 - offset;
                break;
            case "left":
                pos.left = anchorLeft - ownWidth;
                pos.top = anchorTop + anchorHeight / 2 - ownHeight / 2;
                break;
            case "left-top":
                pos.left = anchorLeft - ownWidth;
                pos.top = anchorTop + anchorHeight / 2 - ownHeight + offset;
                break;
            case "left-bottom":
                pos.left = anchorLeft - ownWidth;
                pos.top = anchorTop + anchorHeight / 2 - offset;
                break;
        }

        pos.right = pos.left + ownWidth;
        pos.bottom = pos.top + ownHeight;

        return pos;
    }

    private _isWithinBounds({ top, right, bottom, left }: Position) {
        const { clientWidth, clientHeight } = document.documentElement;
        return top > 0 && left > 0 && right < clientWidth && bottom < clientHeight;
    }

    private _contentChanged() {
        if (this.showing) {
            this.align();
        }
    }

    private _addListeners() {
        if (!this._anchorElement) {
            return;
        }
        switch (this.trigger) {
            case "click":
                this._anchorElement.addEventListener("click", this._clickHandler);
                break;
            case "hover":
                this._anchorElement.addEventListener("mouseenter", this._mouseenterHandler);
                this._anchorElement.addEventListener("mouseleave", this._mouseleaveHandler);
                this.addEventListener("mouseenter", this._mouseenterHandler);
                this.addEventListener("mouseleave", this._mouseleaveHandler);
                break;
            case "focus":
                this._anchorElement.addEventListener("focusin", this._focusHandler);
                this._anchorElement.addEventListener("focusout", this._blurHandler);
                this.addEventListener("focusin", this._focusHandler);
                this.addEventListener("mousedown", this._mousedownHandler);
                this.addEventListener("focusout", this._blurHandler);
                break;
        }

        this.addEventListener("click", this._selfClickHandler);
        document.addEventListener("click", this._documentClickHandler);
        this.addEventListener("mouseleave", this._selfMouseleaveHandler);

        this._mutationObserver.observe(this, { childList: true, subtree: true });
        this._resizeObserver.observe(this);
    }

    private _removeListeners() {
        if (!this._anchorElement) {
            return;
        }
        this._anchorElement.removeEventListener("click", this._clickHandler);
        document.removeEventListener("click", this._documentClickHandler);
        this._anchorElement.removeEventListener("mouseenter", this._mouseenterHandler);
        this._anchorElement.removeEventListener("mouseleave", this._mouseleaveHandler);
        this.removeEventListener("mouseenter", this._mouseenterHandler);
        this.removeEventListener("mouseleave", this._mouseleaveHandler);
        this.removeEventListener("mouseleave", this._selfMouseleaveHandler);
        this.removeEventListener("click", this._selfClickHandler);
        this._anchorElement.removeEventListener("focusin", this._focusHandler);
        this._anchorElement.removeEventListener("focusout", this._blurHandler);
        this.removeEventListener("focusin", this._focusHandler);
        this.removeEventListener("focusout", this._blurHandler);
        this.removeEventListener("mousedown", this._mousedownHandler);
        if (this._scrollParent) {
            this._scrollParent.removeEventListener("scroll", this._scrollHandler);
            this._scrollParent = null;
        }
        this._scrollListenerAttached = false;

        this._mutationObserver.disconnect();
    }

    static styles = [
        shared,
        css`
            :host {
                position: fixed;
                display: block;
                overflow: visible;
                z-index: 10;
                transition:
                    transform 0.3s,
                    opacity 0.3s;
                background: var(--color-bg);
                border-radius: var(--border-radius);
                box-shadow: rgba(0, 0, 0, 0.2) 0 0 3px;
                padding: 0.8em;
                --hover-buffer: var(--popover-hover-buffer, 0.5em);

                --arrow-offset: 12px;
                --arrow-size: 12px;
            }

            :host([positioning="absolute"]) {
                position: absolute;
            }

            :host([non-interactive]) {
                pointer-events: none;
            }

            :host(.unpadded) {
                padding: 0;
            }

            .buffer {
                position: absolute;
                width: calc(100% + var(--hover-buffer) * 2);
                height: calc(100% + var(--hover-buffer) * 2);
                top: calc(-1 * var(--hover-buffer));
                left: calc(-1 * var(--hover-buffer));
                z-index: -1;
            }

            .arrow {
                position: absolute;
                margin: auto;
                width: var(--arrow-size);
                height: var(--arrow-size);
                background: inherit;
                transform: rotate(45deg);
                z-index: -1;
            }

            :host(.bottom) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) -1px -1px 1px;
                left: 0;
                right: 0;
                top: calc(-0.5 * var(--arrow-size));
                border-top-left-radius: 2px;
            }

            :host(.bottom-left) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) -1px -1px 1px;
                right: var(--arrow-offset);
                top: calc(-0.5 * var(--arrow-size));
                border-top-left-radius: 2px;
            }

            :host(.bottom-right) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) -1px -1px 1px;
                left: var(--arrow-offset);
                top: calc(-0.5 * var(--arrow-size));
                border-top-left-radius: 2px;
            }

            :host(.right) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) -1px 1px 1px;
                top: 0;
                bottom: 0;
                left: calc(-0.5 * var(--arrow-size));
                border-bottom-left-radius: 2px;
            }

            :host(.right-top) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) -1px 1px 1px;
                bottom: var(--arrow-offset);
                left: calc(-0.5 * var(--arrow-size));
                border-bottom-left-radius: 2px;
            }

            :host(.right-bottom) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) -1px 1px 1px;
                top: var(--arrow-offset);
                left: calc(-0.5 * var(--arrow-size));
                border-bottom-left-radius: 2px;
            }

            :host(.top) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) 1px 1px 1px;
                left: 0;
                right: 0;
                bottom: calc(-0.5 * var(--arrow-size));
                border-bottom-right-radius: 2px;
            }

            :host(.top-left) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) 1px 1px 1px;
                right: var(--arrow-offset);
                bottom: calc(-0.5 * var(--arrow-size));
                border-bottom-right-radius: 2px;
            }

            :host(.top-right) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) 1px 1px 1px;
                left: var(--arrow-offset);
                bottom: calc(-0.5 * var(--arrow-size));
                border-bottom-right-radius: 2px;
            }

            :host(.left) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) 1px -1px 1px;
                top: 0;
                bottom: 0;
                right: calc(-0.5 * var(--arrow-size));
                border-top-right-radius: 2px;
            }

            :host(.left-top) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) 1px -1px 1px;
                bottom: var(--arrow-offset);
                right: calc(-0.5 * var(--arrow-size));
                border-top-right-radius: 2px;
            }

            :host(.left-bottom) .arrow {
                box-shadow: rgba(0, 0, 0, 0.1) 1px -1px 1px;
                top: var(--arrow-offset);
                right: calc(-0.5 * var(--arrow-size));
                border-top-right-radius: 2px;
            }

            :host(:not(.showing)) {
                opacity: 0;
            }

            :host([class^="bottom"]:not(.showing)),
            :host([class*=" bottom"]:not(.showing)) {
                transform: translate(0, 10px);
            }

            :host([class^="top"]:not(.showing)),
            :host([class*=" top"]:not(.showing)) {
                transform: translate(0, -10px);
            }

            :host([class^="left"]:not(.showing)),
            :host([class*=" left"]:not(.showing)) {
                transform: translate(-10px, 0);
            }

            :host([class^="right"]:not(.showing)),
            :host([class*=" right"]:not(.showing)) {
                transform: translate(10px, 0);
            }

            :host(.tooltip) {
                max-width: 20em;
                font-weight: normal;
                text-align: center;
                font-size: var(--font-size-small);
                background: var(--color-highlight-bg, var(--color-bg));
                color: var(--color-highlight, var(--color-fg));
                border-radius: var(--border-radius);
                padding: 0.5em;
            }

            :host(.popover-menu) {
                display: flex;
                flex-direction: column;
                padding: 0.5em;
            }

            :host(.popover-menu) button {
                background: transparent;
                display: block;
                text-align: left;
                flex: none;
                width: 100%;
            }
        `,
    ];

    render() {
        return html`
            <div class="buffer"></div>
            <div class="arrow"></div>
            ${this.renderContent()}
        `;
    }

    protected renderContent() {
        return this.content || html` <slot> </slot> `;
    }
}
